import { useState } from 'react';
import { NavLink, Outlet } from 'react-router';
import { useApi } from '../api/context';
import { LANGUAGE_NAMES, LOCALES, useI18n } from '../i18n';
import type { MessageKey } from '../i18n/mk';
import { useLiveStatus } from '../live/live';
import { useMerchant, usePanelActive } from '../state/merchant';
import { LockIcon, ZinqLogo } from './ui';

interface NavItem {
  to: string;
  key: MessageKey;
  icon: keyof typeof ICONS;
  /** Needs the web panel plan (shown with a lock while it is off). */
  gated?: boolean;
}

const NAV: readonly NavItem[] = [
  { to: '/', key: 'nav.overview', icon: 'overview' },
  { to: '/payments', key: 'nav.payments', icon: 'payments' },
  { to: '/reconciliation', key: 'nav.reconciliation', icon: 'reconciliation', gated: true },
  { to: '/invoice', key: 'nav.invoice', icon: 'invoice' },
  { to: '/qr', key: 'nav.counterQr', icon: 'qr' },
  { to: '/plan', key: 'nav.plan', icon: 'plan' },
];

const ICONS = {
  overview: 'M4 13h6V4H4zM14 20h6v-9h-6zM14 4v4h6V4zM4 20h6v-3H4z',
  payments: 'M3 7h18M3 12h18M3 17h12',
  reconciliation: 'M7 7h11l-3-3M17 17H6l3 3',
  invoice: 'M6 3h9l4 4v14H6zM14 3v5h5M9 12h6M9 16h6',
  qr: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 18h2v2h-2zM14 18h2M18 14h2',
  plan: 'M12 3l2.6 5.6 6 .7-4.5 4.1 1.2 6L12 16.5 6.7 19.4l1.2-6L3.4 9.3l6-.7z',
} as const;

function NavIcon({ name }: { name: keyof typeof ICONS }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path d={ICONS[name]} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function LanguageSwitch() {
  const { locale, setLocale, t } = useI18n();
  return (
    <div className="lang" role="group" aria-label={t('app.language')}>
      {LOCALES.map((l) => (
        <button
          key={l}
          type="button"
          lang={l}
          className="lang-item"
          data-testid={`lang-${l}`}
          aria-pressed={locale === l}
          onClick={() => setLocale(l)}
        >
          {LANGUAGE_NAMES[l]}
        </button>
      ))}
    </div>
  );
}

function LiveBadge() {
  const { t } = useI18n();
  const status = useLiveStatus();
  const key: MessageKey =
    status === 'open'
      ? 'live.open'
      : status === 'connecting' || status === 'idle'
        ? 'live.connecting'
        : status === 'reconnecting'
          ? 'live.reconnecting'
          : 'live.stopped';
  return (
    <span className={`live live--${status}`} data-testid="live-status" data-status={status}>
      <span className="live-dot" aria-hidden="true" />
      {t(key)}
    </span>
  );
}

/** The signed-in panel: sidebar navigation, top bar, the page. */
export function Layout() {
  const { t } = useI18n();
  const api = useApi();
  const merchant = useMerchant();
  const panelActive = usePanelActive();
  const [signingOut, setSigningOut] = useState(false);

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        {t('app.skipToContent')}
      </a>
      <aside className="sidebar no-print">
        <div className="brand">
          <ZinqLogo size={34} />
          <div>
            <div className="brand-name">ZINQ</div>
            <div className="brand-sub">{t('app.panelName')}</div>
          </div>
        </div>
        <nav aria-label={t('nav.label')}>
          <ul className="nav">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} end={item.to === '/'} className="nav-link">
                  <NavIcon name={item.icon} />
                  <span>{t(item.key)}</span>
                  {item.gated && panelActive === false ? (
                    <span className="nav-lock" title={t('nav.locked')}>
                      <LockIcon size={13} />
                      <span className="visually-hidden">{t('nav.locked')}</span>
                    </span>
                  ) : null}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="sidebar-merchant">
          <div className="sidebar-merchant-name">{merchant.displayName}</div>
          <div className="sidebar-merchant-code">
            {merchant.code} · {merchant.city}
          </div>
        </div>
      </aside>
      <div className="main-col">
        <header className="topbar no-print">
          <div className="topbar-merchant">
            <span className="topbar-name">{merchant.displayName}</span>
            <span className="topbar-code">{merchant.code}</span>
          </div>
          <div className="topbar-actions">
            <LiveBadge />
            <LanguageSwitch />
            <button
              type="button"
              className="btn btn--ghost"
              disabled={signingOut}
              onClick={() => {
                setSigningOut(true);
                void api.logout();
              }}
            >
              {t('app.logout')}
            </button>
          </div>
        </header>
        <main id="main" className="content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
